import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  IndianRupee, Clock, CheckCircle2, CalendarDays, ListChecks, ShieldCheck,
  Phone, ArrowLeft, Users, Sparkles,
} from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import EnquiryForm from "@/components/EnquiryForm";
import { getPooja, categoryLabel, resolveImg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink, poojaWaMessage } from "@/lib/whatsapp";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Block = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-border rounded-xl p-6">
    <h3 className="flex items-center gap-2 font-heading text-2xl text-secondary"><Icon className="h-5 w-5 text-primary" /> {title}</h3>
    <div className="mt-3 text-muted-foreground leading-relaxed">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-2">
    {items?.map((it) => (
      <li key={it} className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" /><span>{it}</span></li>
    ))}
  </ul>
);

const faqJsonLd = (faqs) => (faqs?.length ? {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question", name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
} : undefined);

const PoojaHero = ({ p, heroImg }) => (
  <section className="relative">
    <div className="relative h-[42vh] min-h-[320px]">
      <img src={heroImg} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2C1E16]/95 via-[#2C1E16]/55 to-[#2C1E16]/30" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 h-full flex flex-col justify-end pb-8">
        <Link to="/poojas" className="inline-flex items-center gap-1.5 text-white/80 text-sm hover:text-gold mb-4" data-testid="back-to-poojas"><ArrowLeft className="h-4 w-4" /> All Poojas</Link>
        <span className="inline-flex w-fit items-center rounded-full border border-gold bg-black/30 px-3 py-1 text-xs font-semibold text-gold">{categoryLabel(p.category)}</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl text-white tracking-tight max-w-3xl">{p.name}</h1>
        <p className="mt-2 text-white/85 max-w-2xl">{p.short_purpose}</p>
      </div>
    </div>
  </section>
);

const PoojaBody = ({ p }) => (
  <div className="lg:col-span-2 space-y-6">
    <Block icon={Sparkles} title="Spiritual Purpose & Benefits">
      <p className="mb-4">{p.full_description}</p>
      <List items={p.benefits} />
    </Block>

    {p.who_should_do?.length > 0 && (
      <Block icon={Users} title="Who Should Do This Pooja">
        <List items={p.who_should_do} />
      </Block>
    )}

    <div className="grid sm:grid-cols-2 gap-6">
      {p.when_performed && <Block icon={CalendarDays} title="When It's Performed"><p>{p.when_performed}</p></Block>}
      {p.duration && <Block icon={Clock} title="Duration"><p>{p.duration}</p></Block>}
    </div>

    {p.process_steps?.length > 0 && (
      <Block icon={ListChecks} title="Process">
        <ol className="space-y-3">
          {p.process_steps.map((s, i) => (
            <li key={s} className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white text-xs font-bold">{i + 1}</span><span>{s}</span></li>
          ))}
        </ol>
      </Block>
    )}

    <div className="grid sm:grid-cols-2 gap-6">
      {p.what_included?.length > 0 && <Block icon={CheckCircle2} title="What's Included"><List items={p.what_included} /></Block>}
      {p.required_details?.length > 0 && <Block icon={ListChecks} title="Details We Need From You"><List items={p.required_details} /></Block>}
    </div>

    {p.images?.length > 1 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {p.images.map((img, i) => <img key={img} src={resolveImg(img)} alt={`${p.name} ${i + 1}`} loading="lazy" className="rounded-xl object-cover aspect-square w-full" />)}
      </div>
    )}

    {p.trust_note && (
      <div className="flex gap-3 bg-cream border border-gold/40 rounded-xl p-6">
        <ShieldCheck className="h-6 w-6 text-secondary shrink-0" />
        <p className="text-secondary font-medium">{p.trust_note}</p>
      </div>
    )}

    {p.pooja_faqs?.length > 0 && (
      <div>
        <h3 className="font-heading text-3xl text-secondary mb-4">Frequently Asked Questions</h3>
        <Accordion type="single" collapsible className="space-y-3" data-testid="pooja-faq-accordion">
          {p.pooja_faqs.map((f, i) => (
            <AccordionItem key={f.question} value={`f${i}`} className="bg-white border border-border rounded-xl px-5">
              <AccordionTrigger className="text-left font-medium text-secondary hover:no-underline">{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    )}
  </div>
);

const PoojaSidebar = ({ p, waMsg }) => {
  const site = useSite();
  return (
    <aside className="lg:col-span-1">
      <div className="lg:sticky lg:top-24 space-y-5">
        <div className="bg-white border border-border rounded-xl p-6">
          <span className="text-xs text-muted-foreground">Starting from</span>
          <div className="flex items-center text-secondary"><IndianRupee className="h-6 w-6" /><span className="text-4xl font-semibold">{p.price?.toLocaleString("en-IN")}</span></div>
          {p.price_note && <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{p.price_note}</p>}
          <a href={waLink(site.whatsapp_number, waMsg)} target="_blank" rel="nofollow noopener noreferrer" data-testid="detail-whatsapp-button" className="mt-5 flex items-center justify-center gap-2 rounded-md bg-whatsapp px-6 py-3.5 font-semibold text-white hover:opacity-90">
            <Phone className="h-4 w-4" /> Book on WhatsApp
          </a>
          <p className="mt-3 text-center text-xs text-muted-foreground">Quick honest guidance · no pressure</p>
        </div>

        <div className="bg-white border border-border rounded-xl p-6" id="enquire">
          <h3 className="font-heading text-2xl text-secondary mb-1">Send an Enquiry</h3>
          <p className="text-sm text-muted-foreground mb-4">Our priest team will contact you shortly.</p>
          <EnquiryForm poojaName={p.name} poojaSlug={p.slug} />
        </div>
      </div>
    </aside>
  );
};

const PoojaDetail = () => {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["pooja", slug], queryFn: () => getPooja(slug) });

  if (isLoading) return <Layout><div className="mx-auto max-w-7xl px-6 py-24 text-muted-foreground">Loading…</div></Layout>;
  if (isError || !p) return <Layout><div className="mx-auto max-w-7xl px-6 py-24"><p className="text-muted-foreground">Pooja not found.</p><Link to="/poojas" className="text-primary">Back to poojas</Link></div></Layout>;

  const heroImg = resolveImg((p.images && p.images[0]) || "");
  const waMsg = poojaWaMessage(p.name);

  return (
    <Layout waMessage={waMsg}>
      <Seo
        title={p.meta_title || `${p.name} in Gokarna`}
        description={p.meta_description || p.short_purpose}
        jsonLd={faqJsonLd(p.pooja_faqs)}
      />
      <PoojaHero p={p} heroImg={heroImg} />
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-12 grid lg:grid-cols-3 gap-10">
        <PoojaBody p={p} />
        <PoojaSidebar p={p} waMsg={waMsg} />
      </section>
    </Layout>
  );
};

export default PoojaDetail;
